/**
 * Profond — Cinematic 3D motion
 *
 * Three progressive-enhancement layers, all transform-only and
 * rAF-throttled for performance:
 *   1. 3D reveal on scroll  (fade + rise + un-tilt as elements enter)
 *   2. Parallax depth        ([data-lux-parallax] + section backgrounds)
 *   3. Pointer 3D tilt       (luxury hover on product / collection cards)
 *
 * Everything is disabled when the visitor prefers reduced motion, and
 * tilt is limited to fine pointers (mouse / trackpad) only.
 */
(() => {
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  if (reduceMotion) return;

  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const rAF = window.requestAnimationFrame.bind(window);

  /* ---------------------------------------------------------------
     1. 3D REVEAL ON SCROLL
  --------------------------------------------------------------- */
  if ('IntersectionObserver' in window) {
    const REVEAL_SELECTOR =
      '.shopify-section > *, .shopify-section .group-block, .product-card, .collection-card';

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('lux-in');
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const registerReveal = (root = document) => {
      root.querySelectorAll(REVEAL_SELECTOR).forEach((el, index) => {
        if (el.dataset.luxReveal) return;
        el.dataset.luxReveal = '1';
        el.classList.add('lux-reveal');
        el.style.setProperty('--lux-delay', `${Math.min(index % 6, 5) * 70}ms`);
        observer.observe(el);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => registerReveal(), {
        once: true,
      });
    } else {
      registerReveal();
    }

    document.addEventListener('shopify:section:load', (event) =>
      registerReveal(event.target)
    );
  }

  /* ---------------------------------------------------------------
     2. PARALLAX DEPTH
     Auto-tags section background images + any opted-in element.
  --------------------------------------------------------------- */
  const collectParallax = () => {
    document
      .querySelectorAll('.background-image-container img')
      .forEach((img) => {
        if (img.dataset.luxParallax) return;
        // Ken-Burns (CSS) owns `scale`; parallax owns `transform`, so the
        // two compose cleanly. The animated scale (1.06–1.16) also gives
        // enough overflow that the translate never reveals an edge.
        img.dataset.luxParallax = '0.1';
      });
    return Array.from(document.querySelectorAll('[data-lux-parallax]'));
  };

  let parallaxEls = [];
  let ticking = false;

  // Parallax is desktop-only: on phones it's skipped for smoother scrolling
  // (Ken-Burns + reveals still run). Transforms are reset if a tablet/phone
  // rotates below the breakpoint.
  const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

  const applyParallax = () => {
    if (!isDesktop()) {
      for (const el of parallaxEls) el.style.transform = '';
      ticking = false;
      return;
    }
    const viewportH = window.innerHeight;
    for (const el of parallaxEls) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewportH + 200) continue;
      const center = rect.top + rect.height / 2;
      const progress = (center - viewportH / 2) / viewportH; // ~ -1..1
      const depth = parseFloat(el.dataset.luxParallax) || 0.12;
      const shift = (-progress * depth * 100).toFixed(2);
      el.style.transform = `translate3d(0, ${shift}px, 0)`;
    }
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    rAF(applyParallax);
  };

  const refreshParallax = () => {
    parallaxEls = collectParallax();
    onScroll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshParallax, {
      once: true,
    });
  } else {
    refreshParallax();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', refreshParallax);

  /* ---------------------------------------------------------------
     3. POINTER 3D TILT (fine pointers only)
  --------------------------------------------------------------- */
  if (finePointer) {
    const MAX_TILT = 6; // degrees
    let activeCard = null;
    let tiltQueued = false;
    let lastX = 0;
    let lastY = 0;

    const renderTilt = () => {
      tiltQueued = false;
      if (!activeCard) return;
      const rect = activeCard.getBoundingClientRect();
      const px = (lastX - rect.left) / rect.width - 0.5; // -0.5..0.5
      const py = (lastY - rect.top) / rect.height - 0.5;
      const rotateY = (px * MAX_TILT * 2).toFixed(2);
      const rotateX = (-py * MAX_TILT * 2).toFixed(2);
      // translateZ lifts the card toward the viewer for real depth.
      activeCard.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(18px)`;
    };

    const onPointerMove = (event) => {
      lastX = event.clientX;
      lastY = event.clientY;
      if (!tiltQueued) {
        tiltQueued = true;
        rAF(renderTilt);
      }
    };

    const releaseCard = () => {
      if (!activeCard) return;
      activeCard.classList.remove('lux-tilting');
      activeCard.style.transform = '';
      activeCard.removeEventListener('pointermove', onPointerMove);
      activeCard.removeEventListener('pointerleave', releaseCard);
      activeCard = null;
    };

    document.addEventListener('pointerover', (event) => {
      const card = event.target.closest('.product-card, .collection-card');
      if (!card || card === activeCard) return;
      releaseCard();
      activeCard = card;
      activeCard.classList.add('lux-tilting');
      activeCard.addEventListener('pointermove', onPointerMove, {
        passive: true,
      });
      activeCard.addEventListener('pointerleave', releaseCard);
    });
  }
})();
